export interface SliderMark {
  value: number;
  label?: string;
}

export interface JointUnit {
  unit: "°" | "mm";
  decimals: number;
  step: number;
  snapTolerance: number;
  toDisplay: (nativeValue: number) => number;
  toNative: (displayValue: number) => number;
  ticks: (minNative: number, maxNative: number) => number[];
}

const ROTATION_TICKS = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
const RANGE_EPS = 1e-6;

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function normalizeDisplayValue(value: number, decimals: number): number {
  const normalized = roundTo(value, Math.max(decimals, 4));
  const zeroThreshold = 10 ** (-decimals) / 2;
  return Math.abs(normalized) < zeroThreshold ? 0 : normalized;
}

function uniqueSorted(values: number[], decimals: number): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const result: number[] = [];
  for (const value of sorted) {
    const normalized = normalizeDisplayValue(value, decimals);
    if (result.length === 0 || Math.abs(result[result.length - 1] - normalized) > RANGE_EPS) {
      result.push(normalized);
    }
  }
  return result;
}

const prismaticUnit: JointUnit = {
  unit: "mm",
  decimals: 1,
  step: 0.5,
  snapTolerance: 2,
  toDisplay: (nativeValue: number) => nativeValue * 1000,
  toNative: (displayValue: number) => displayValue / 1000,
  ticks: (minNative: number, maxNative: number) => {
    const dMin = (minNative * 1000);
    const dMax = (maxNative * 1000);
    const lo = Math.min(dMin, dMax);
    const hi = Math.max(dMin, dMax);
    return uniqueSorted([dMin, 0, dMax].filter((tick) => tick >= lo - RANGE_EPS && tick <= hi + RANGE_EPS), 1);
  },
};

const rotationUnit: JointUnit = {
  unit: "°",
  decimals: 0,
  step: 1,
  snapTolerance: 8,
  toDisplay: (nativeValue: number) => (nativeValue * 180) / Math.PI,
  toNative: (displayValue: number) => (displayValue * Math.PI) / 180,
  ticks: (minNative: number, maxNative: number) => {
    const dMin = (minNative * 180) / Math.PI;
    const dMax = (maxNative * 180) / Math.PI;
    const lo = Math.min(dMin, dMax);
    const hi = Math.max(dMin, dMax);
    return ROTATION_TICKS.filter((tick) => tick >= lo - RANGE_EPS && tick <= hi + RANGE_EPS);
  },
};

export function getJointUnit(jointType: string | null | undefined): JointUnit {
  return jointType === "prismatic" ? prismaticUnit : rotationUnit;
}

export function snapDisplayValue(value: number, unit: JointUnit, minNative: number, maxNative: number): number {
  const ticks = unit.ticks(minNative, maxNative);
  let bestTick: number | null = null;
  let bestDistance = unit.snapTolerance;
  for (const tick of ticks) {
    const distance = Math.abs(tick - value);
    if (distance <= bestDistance) {
      bestDistance = distance;
      bestTick = tick;
    }
  }
  return bestTick === null ? value : bestTick;
}

export function formatDisplayValue(value: number, unit: JointUnit): string {
  return `${normalizeDisplayValue(value, unit.decimals).toFixed(unit.decimals)} ${unit.unit}`;
}

// Only the two limits and zero get text. Labelling every cardinal as well put
// five labels on a ±135° joint, and the ±90 ones collided with the limits as
// soon as the panel was narrow. The unlabelled ticks still show where the
// slider will snap, which is what they are there for.
function shouldLabelRotationTick(value: number, minDisplay: number, maxDisplay: number): boolean {
  return (
    Math.abs(value - minDisplay) <= RANGE_EPS ||
    Math.abs(value - maxDisplay) <= RANGE_EPS ||
    value === 0
  );
}

export function buildSliderMarks(unit: JointUnit, minNative: number, maxNative: number): SliderMark[] {
  const minDisplay = normalizeDisplayValue(unit.toDisplay(minNative), unit.decimals);
  const maxDisplay = normalizeDisplayValue(unit.toDisplay(maxNative), unit.decimals);
  const tickValues = uniqueSorted([...unit.ticks(minNative, maxNative), minDisplay, maxDisplay], unit.decimals);

  return tickValues.map((value) => {
    const label = unit.unit === "mm"
      ? formatDisplayValue(value, unit)
      : (shouldLabelRotationTick(value, minDisplay, maxDisplay) ? formatDisplayValue(value, unit) : undefined);
    return label === undefined ? { value } : { value, label };
  });
}
